/**
 * Shared public-page chrome — PadelPulse
 *
 * The public marketing surface (landing, pricing, and the legal sheets) shares
 * one header and one footer so navigation and legal links live in a single
 * place. Following the app's vanilla-TS model, these helpers return HTML
 * strings for `innerHTML` composition, and `setupPublicChrome()` wires the
 * interactive bits (language toggle + login button) after the HTML is mounted.
 *
 * The footer intentionally points at the real public routes (`#pricing`,
 * `#privacy`, `#terms`) rather than the earlier `#landing` placeholders now
 * that those views exist.
 */

import { t } from "../i18n/config";
import { renderLanguageToggle, setupLanguageToggle } from "../i18n/languageToggle";

/** Public routes reachable without a session, used to highlight the active nav item. */
export type PublicNav = "landing" | "pricing" | "privacy" | "terms";

/**
 * Public brand header with the primary nav (Pricing), the language toggle, and
 * the returning-organizer login button (`#landing-login-btn`, wired by
 * `setupPublicChrome`). `active` highlights the current nav entry.
 */
export function renderPublicHeader(active: PublicNav = "landing"): string {
  const pricingActive =
    active === "pricing"
      ? "text-primary dark:text-primary daylight:text-day-ink"
      : "text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft hover:text-primary dark:hover:text-primary daylight:hover:text-day-ink";

  return `
    <header class="w-full border-b border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between">
        <a href="#landing" class="flex items-center gap-3 group hover:opacity-80 transition-opacity">
          <img src="/brand/icon-reverse.svg" alt="PadelPulse" class="size-10 hidden dark:block" />
          <img src="/brand/icon-color.svg" alt="PadelPulse" class="size-10 hidden daylight:block" />
          <h2 class="text-2xl leading-none text-slate-900 dark:text-white daylight:text-day-ink"><span class="font-medium">PADEL</span><span class="font-extrabold">PULSE</span></h2>
        </a>

        <!--
          Desktop nav (md+): Pricing, language toggle, and login sit inline as
          originally styled. On narrow viewports these collapse into the
          hamburger drawer below, so this row is hidden until md.
        -->
        <div class="hidden md:flex items-center gap-3 sm:gap-4">
          <!-- Primary public nav. Kept minimal so the login CTA stays prominent. -->
          <a href="#pricing" aria-current="${active === "pricing" ? "page" : "false"}" class="text-sm font-semibold transition-colors ${pricingActive}">${t("nav.pricing")}</a>

          <!-- Language Selector -->
          ${renderLanguageToggle()}

          <!-- Returning-organizer login (secondary), collapses to icon-only on mobile. -->
          <button id="landing-login-btn" class="btn-secondary" aria-label="${t("nav.login")}">
            <span class="material-symbols-outlined">login</span>
            <span class="hidden sm:inline">${t("nav.login")}</span>
          </button>
        </div>

        <!-- Mobile hamburger toggle (mirrors the authenticated shell's pattern). -->
        <button
          id="public-nav-toggle"
          class="md:hidden size-10 p-0 flex items-center justify-center border border-slate-400 daylight:border-day-ink-muted hover:bg-slate-100 dark:hover:bg-slate-800 daylight:hover:bg-day-sunken rounded-lg transition"
          aria-label="${t("nav.menu")}"
          aria-controls="public-mobile-nav"
          aria-expanded="false"
        >
          <span class="material-symbols-outlined">menu</span>
        </button>
      </div>

      <!--
        Mobile drawer (hidden by default, md:hidden so it never shows on
        desktop). Groups Pricing, the language toggle, and login. Toggling is
        wired in setupPublicChrome via the DOM "hidden" class.
      -->
      <nav id="public-mobile-nav" class="hidden md:hidden border-t border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 bg-background-light dark:bg-background-dark daylight:bg-day-surface" aria-label="${t("nav.menu")}">
        <div class="px-4 sm:px-6 py-4 space-y-3">
          <a href="#pricing" aria-current="${active === "pricing" ? "page" : "false"}" class="block px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${pricingActive} hover:bg-slate-100 dark:hover:bg-slate-800 daylight:hover:bg-day-sunken">${t("nav.pricing")}</a>

          <!-- Language Selector -->
          <div class="px-4 py-2">${renderLanguageToggle()}</div>

          <!-- Returning-organizer login (secondary). Label always shown in the drawer. -->
          <button id="landing-login-btn-mobile" class="btn-secondary w-full justify-center" aria-label="${t("nav.login")}">
            <span class="material-symbols-outlined">login</span>
            <span>${t("nav.login")}</span>
          </button>
        </div>
      </nav>
    </header>
  `;
}

/**
 * SEO-friendly public footer. Legal and Pricing entries link to the dedicated
 * public routes; Resources still points at safe placeholders (help scrolls to
 * the landing page, support is a mailto).
 */
export function renderPublicFooter(): string {
  const year = new Date().getFullYear();

  const columns: { heading: string; links: { label: string; href: string }[] }[] = [
    {
      heading: t("landing.footer.platform"),
      links: [
        { label: t("landing.footer.features"), href: "#landing" },
        { label: t("nav.pricing"), href: "#pricing" },
        { label: t("landing.footer.analytics"), href: "#landing" },
      ],
    },
    {
      heading: t("landing.footer.forOrganizers"),
      links: [
        { label: t("landing.footer.tournaments"), href: "#landing" },
        { label: t("landing.footer.leagues"), href: "#landing" },
      ],
    },
    {
      heading: t("landing.footer.resources"),
      links: [
        { label: t("landing.footer.helpCenter"), href: "#landing" },
        { label: t("landing.footer.support"), href: "mailto:support@padelpulse.club" },
      ],
    },
    {
      heading: t("landing.footer.legal"),
      links: [
        { label: t("landing.footer.privacy"), href: "#privacy" },
        { label: t("landing.footer.terms"), href: "#terms" },
      ],
    },
  ];

  const linkClass =
    "text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft hover:text-primary dark:hover:text-primary daylight:hover:text-day-ink transition-colors";

  const navColumns = columns
    .map(
      (col) => `
        <nav aria-label="${col.heading}" class="space-y-3">
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${col.heading}</h3>
          <ul class="space-y-2">
            ${col.links
              .map(
                (link) =>
                  `<li><a href="${link.href}" class="${linkClass}">${link.label}</a></li>`
              )
              .join("")}
          </ul>
        </nav>`
    )
    .join("");

  return `
    <footer class="border-t border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 bg-slate-50 dark:bg-slate-900/40 daylight:bg-day-sunken">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
          <!-- Brand blurb -->
          <div class="col-span-2 md:col-span-4 lg:col-span-1 space-y-3">
            <div class="flex items-center gap-3">
              <img src="/brand/icon-reverse.svg" alt="PadelPulse" class="size-10 hidden dark:block" />
              <img src="/brand/icon-color.svg" alt="PadelPulse" class="size-10 hidden daylight:block" />
              <h2 class="text-2xl leading-none text-slate-900 dark:text-white daylight:text-day-ink"><span class="font-medium">PADEL</span><span class="font-extrabold">PULSE</span></h2>
            </div>
            <p class="text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft max-w-xs">
              ${t("landing.footer.blurb")}
            </p>
          </div>

          ${navColumns}
        </div>

        <div class="mt-12 pt-8 border-t border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
          <p class="text-sm text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">
            ${t("landing.footer.copyright", { year })}
          </p>
        </div>
      </div>
    </footer>
  `;
}

/**
 * Wire the shared header controls within `container`: the language toggle and
 * the login button (routes to `#login`, which the router resolves to the login
 * modal). Call once after the header HTML is mounted.
 */
export function setupPublicChrome(container: HTMLElement): void {
  setupLanguageToggle(container);

  // Both the desktop and mobile-drawer login buttons route to the login modal.
  const loginBtns = container.querySelectorAll("#landing-login-btn, #landing-login-btn-mobile");
  loginBtns.forEach((btn) =>
    btn.addEventListener("click", () => {
      window.location.hash = "login";
    })
  );

  // Mobile hamburger: toggle the drawer by flipping its `hidden` class, mirroring
  // the authenticated shell (see LayoutManager.setupHeaderListeners). State lives
  // in the DOM — there is no framework component state in this vanilla-TS app.
  const navToggle = container.querySelector("#public-nav-toggle");
  const mobileNav = container.querySelector("#public-mobile-nav");
  navToggle?.addEventListener("click", () => {
    const isOpen = mobileNav?.classList.toggle("hidden") === false;
    navToggle.setAttribute("aria-expanded", String(isOpen));
  });

  // Close the drawer once a link inside it is followed.
  container.querySelectorAll("#public-mobile-nav a").forEach((link) =>
    link.addEventListener("click", () => {
      mobileNav?.classList.add("hidden");
      navToggle?.setAttribute("aria-expanded", "false");
    })
  );
}
